/**
 * The registry a set keeps when pins go, so no edit leaves a group the set cannot hold.
 *
 * A row lives while something pins it, which is what makes an element reusable across
 * drawings. A group is pinned nowhere by rule, so it is judged by its members instead: it
 * stays while two or more of them stay, and a group left with one member dissolves into it.
 * The survivor takes the group's place in the list and any override the group carried, so it
 * goes on showing the numeral the group showed.
 *
 * The same rule runs in `packages/pipeline/src/patent_pipeline/figure_registry.py`, and both
 * are held to `packages/contracts/fixtures/registry-after-unpin.json`.
 *
 * The two edits the attorney makes by dragging, nesting one component under another and
 * taking one out, end in the same rule, so a group of one never reaches the server.
 */

import type { ElementRow } from "./element-registry";
import { membership } from "./numbering";

/** What this rule reads of a registry row, and what it rewrites on a survivor. */
export interface RegistryRow {
  element_id: string;
  kind?: "element" | "group";
  group_id?: string | null;
  numeral_override?: number | null;
}

/** A group of one is an element with a letter it does not need; two is the least a set can be. */
export const FEWEST_MEMBERS = 2;

/**
 * The rows with every group of fewer than two members dissolved, and nothing else changed.
 *
 * A group of one dissolves into its member, which takes the group's place in the list and any
 * override the group carried; a group of none goes. Every edit that can shrink a group ends
 * here: a pin removed, a member taken out. The rows given are not changed.
 */
export function dissolveSmallGroups<Row extends RegistryRow>(
  registry: readonly Row[],
): Row[] {
  const members = new Map<string, Row[]>(
    registry
      .filter((row) => row.kind === "group")
      .map((row) => [row.element_id, []]),
  );
  for (const row of registry) {
    if (row.kind !== "group" && row.group_id != null)
      members.get(row.group_id)?.push(row);
  }
  const dissolved = new Set(
    [...members]
      .filter(([, held]) => held.length < FEWEST_MEMBERS)
      .map(([groupId]) => groupId),
  );

  const kept: Row[] = [];
  for (const row of registry) {
    if (row.kind === "group" && dissolved.has(row.element_id)) {
      for (const sole of members.get(row.element_id) ?? [])
        kept.push({
          ...sole,
          group_id: null,
          numeral_override: row.numeral_override ?? sole.numeral_override,
        });
    } else if (
      row.kind === "group" ||
      row.group_id == null ||
      !dissolved.has(row.group_id)
    ) {
      kept.push(row);
    }
  }
  return kept;
}

/**
 * The rows that stay when only `pinned` elements are still pinned, in their order.
 *
 * A row no drawing pins goes; a group, pinned nowhere by rule, is then judged by the members
 * left to it. The rows given are not changed: a dissolved group's survivor is a copy.
 */
export function registryAfter<Row extends RegistryRow>(
  registry: readonly Row[],
  pinned: ReadonlySet<string>,
): Row[] {
  return dissolveSmallGroups(
    registry.filter(
      (row) => row.kind === "group" || pinned.has(row.element_id),
    ),
  );
}

/** A to z: the letters a member shows run out after 26, and the API refuses a 27th. */
export const MOST_MEMBERS = 26;

/** Where the last row of a group sits: its last member, or the group row when it has none yet. */
function endOf(registry: readonly ElementRow[], groupId: string): number {
  let end = -1;
  registry.forEach((row, index) => {
    if (row.element_id === groupId || row.group_id === groupId) end = index;
  });
  return end;
}

/**
 * Drop one component on another, so both become lettered members of one numeral.
 *
 * On a plain component, a group row takes the target's place, its title and any typed
 * numeral, and is not confirmed until the attorney says so; the target and the dropped
 * component become its members. On a member, the dropped component joins that member's group
 * after its last member, so it takes the next letter. A group row takes no drop and is never
 * dragged: a group inside a group is a set the API refuses. A dropped member leaves
 * its old group, which dissolves when one member is left. A member carries no typed numeral,
 * so the dropped one's goes.
 *
 * The same set comes back for a drop that changes nothing: on itself, of or on a group row, or
 * inside its own group. `"full"` comes back for a drop that would make a 27th member.
 */
export function nest(
  registry: readonly ElementRow[],
  {
    dragged,
    target,
    groupId,
  }: { dragged: string; target: string; groupId: string },
): readonly ElementRow[] | "full" {
  const moving = registry.find((r) => r.element_id === dragged);
  const onto = registry.find((r) => r.element_id === target);
  if (
    !moving ||
    !onto ||
    dragged === target ||
    moving.kind === "group" ||
    onto.kind === "group"
  )
    return registry;
  const groupOf = membership(registry);
  const joining = groupOf(onto);
  if (joining !== null && groupOf(moving) === joining) return registry;
  if (
    joining !== null &&
    registry.filter((r) => groupOf(r) === joining).length >= MOST_MEMBERS
  )
    return "full";

  const without = registry.filter((r) => r.element_id !== dragged);
  const moved: ElementRow = {
    ...moving,
    group_id: joining ?? groupId,
    numeral_override: null,
  };
  if (joining !== null) {
    const end = endOf(without, joining) + 1;
    return dissolveSmallGroups([
      ...without.slice(0, end),
      moved,
      ...without.slice(end),
    ]);
  }
  const at = without.findIndex((r) => r.element_id === target);
  const group: ElementRow = {
    element_id: groupId,
    title: onto.title,
    confirmed: false,
    kind: "group",
    group_id: null,
    numeral_override: onto.numeral_override ?? null,
  };
  return dissolveSmallGroups([
    ...without.slice(0, at),
    group,
    { ...onto, group_id: groupId, numeral_override: null },
    moved,
    ...without.slice(at + 1),
  ]);
}

/**
 * Take a member out of its group, keeping its place in the list.
 *
 * The letters after it close up. A group left with one member dissolves into it, which then
 * takes the group's place and any typed numeral, so it keeps the number the group showed.
 */
export function takeOut(
  registry: readonly ElementRow[],
  memberId: string,
): readonly ElementRow[] {
  const member = registry.find((r) => r.element_id === memberId);
  if (!member || membership(registry)(member) === null) return registry;
  return dissolveSmallGroups(
    registry.map((r) => (r === member ? { ...r, group_id: null } : r)),
  );
}

/** The drop an Undo reverses: the component dragged, and the group it joined. */
export interface UndoneDrop {
  elementId: string;
  from: string;
}

/**
 * Put back a group a take-out or a drop dissolved, as it was: the Undo on the toast.
 *
 * The group row comes back with its identity, title, confirmation and typed number, where its
 * first member now sits, and the members that are still free follow it in their old order. A
 * drop is undone whole: the dragged member first leaves the group it joined, while it is still
 * there, which dissolves a group the drop made back into its target. A member that has since
 * joined another group stays there, and with fewer than two free the group cannot stand, so
 * the set comes back unchanged, as it does while the group is still there.
 */
export function restoreGroup(
  current: readonly ElementRow[],
  {
    group,
    memberIds,
    undoing,
  }: {
    group: ElementRow;
    memberIds: readonly string[];
    undoing?: UndoneDrop;
  },
): readonly ElementRow[] {
  if (current.some((r) => r.element_id === group.element_id)) return current;
  const dragged = current.find((r) => r.element_id === undoing?.elementId);
  const registry =
    undoing !== undefined && dragged?.group_id === undoing.from
      ? takeOut(current, undoing.elementId)
      : current;
  const groupOf = membership(registry);
  const free = new Set(
    registry
      .filter((r) => memberIds.includes(r.element_id) && groupOf(r) === null)
      .map((r) => r.element_id),
  );
  if (free.size < FEWEST_MEMBERS) return current;

  const members = memberIds.flatMap((id) => {
    const member = registry.find((r) => r.element_id === id);
    return member !== undefined && free.has(id)
      ? [{ ...member, group_id: group.element_id, numeral_override: null }]
      : [];
  });
  // The rows before the first free member keep their places, and the group goes after them.
  const at = registry.findIndex((r) => free.has(r.element_id));
  const before = registry.slice(0, at);
  const after = registry.slice(at).filter((r) => !free.has(r.element_id));
  return [...before, group, ...members, ...after];
}

/** A group an edit dissolved, as it was, which is what the Undo on its toast needs. */
export interface DissolvedGroup {
  group: ElementRow;
  memberIds: string[];
  /** The drop that dissolved it, which its Undo reverses first; none for a take-out. */
  undoing?: UndoneDrop;
}

/** The groups the first set holds and the second does not, each with its members as they were. */
export function dissolvedBetween(
  before: readonly ElementRow[],
  after: readonly ElementRow[],
): DissolvedGroup[] {
  const kept = new Set(after.map((r) => r.element_id));
  const groupOf = membership(before);
  return before
    .filter((r) => r.kind === "group" && !kept.has(r.element_id))
    .map((group) => ({
      group,
      memberIds: before
        .filter((r) => groupOf(r) === group.element_id)
        .map((r) => r.element_id),
    }));
}

/** One row of a figure's component table: a plain element, a group, or a member under it. */
export interface TableRow {
  elementId: string;
  place: "element" | "group" | "member";
}

/**
 * The rows a figure's component table draws, from the elements pinned on it in their order.
 *
 * A group is pinned nowhere, so it is drawn for its members: above the first of them that this
 * figure carries, with each member it carries under it in the group's own order. Members the
 * figure does not carry are not drawn here.
 */
export function tableRows(
  registry: readonly ElementRow[],
  onFigure: readonly string[],
): TableRow[] {
  const groupOf = membership(registry);
  const here = new Set(onFigure);
  const drawn = new Set<string>();
  const rows: TableRow[] = [];
  for (const elementId of onFigure) {
    const row = registry.find((r) => r.element_id === elementId);
    const group = row === undefined ? null : groupOf(row);
    if (group === null) {
      rows.push({ elementId, place: "element" });
    } else if (!drawn.has(group)) {
      drawn.add(group);
      rows.push({ elementId: group, place: "group" });
      for (const member of registry)
        if (groupOf(member) === group && here.has(member.element_id))
          rows.push({ elementId: member.element_id, place: "member" });
    }
  }
  return rows;
}

/**
 * Confirm the titled group rows a figure's table shows, which Confirm all covers with the
 * elements: a group is drawn on a figure that carries any of its members.
 */
export function confirmTitledGroups(
  registry: readonly ElementRow[],
  onFigure: readonly string[],
): readonly ElementRow[] {
  const shown = new Set(
    tableRows(registry, onFigure)
      .filter((row) => row.place === "group")
      .map((row) => row.elementId),
  );
  const due = (row: ElementRow) =>
    shown.has(row.element_id) && !row.confirmed && row.title.trim() !== "";
  if (!registry.some(due)) return registry;
  return registry.map((row) => (due(row) ? { ...row, confirmed: true } : row));
}
